import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useAppContext } from '../../context/AppContext';
import api from '../../services/api';
import { budgetService } from '../../services/budgetService';
import useTheme from '../../hooks/useTheme';
import MantleBrand from './MantleBrand';
import ChangePasswordModal from './ChangePasswordModal';
import SearchSelect from '../SearchSelect';

/**
 * TopHeader
 *
 * Role and Site dropdowns are populated from the user's real assignments
 * (loaded into AppContext at login by authService).
 *
 * On mount it also hits GET /api/users/me/assignments to refresh in case
 * an admin changed anything mid-session. If that endpoint isn't wired up
 * yet, the call silently fails and we fall back to the login-time data.
 *
 * Values:
 *   - Role  → urid (string, matches localStorage.roleUrid / x-role-urid header)
 *   - Site  → site_code (string), or empty string for "All sites" (which
 *             clears localStorage.siteCode and sends no x-site-code header)
 *
 * Version dropdown loads active rows from bud_versions via
 * GET /api/budgets/versions, showing the description. Falls back to the
 * login-time selection if the call fails.
 */
const TopHeader = () => {
  const {
    selectedVersion, setSelectedVersion,
    selectedSite, setSelectedSite,
    selectedRole, setSelectedRole,
    assignments, setAssignments,
  } = useAppContext();

  // Active budget versions for the selector (loaded from bud_versions)
  const [versions, setVersions] = useState([]);

  // ---------------------------------------------------------------------------
  // Refresh assignments from server on mount (best-effort)
  // ---------------------------------------------------------------------------
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get('/users/me/assignments');
        if (cancelled) return;
        if (Array.isArray(res.data?.assignments)) {
          setAssignments(res.data.assignments);
        }
      } catch (err) {
        // Endpoint not available yet, or network error — fall back to
        // whatever AppContext already has from login. No-op.
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ---------------------------------------------------------------------------
  // Load active budget versions on mount (best-effort)
  // ---------------------------------------------------------------------------
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await budgetService.getVersions();
        if (cancelled) return;
        const list = Array.isArray(res) ? res : res?.data;
        if (Array.isArray(list)) {
          setVersions(list);
        }
      } catch (err) {
        // Endpoint not available yet, or network error — the dropdown falls
        // back to showing just the currently-selected version. No-op.
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // ---------------------------------------------------------------------------
  // Derive dropdown options from assignments
  // ---------------------------------------------------------------------------

  // Unique roles the user has — one entry per urid.
  const roleOptions = useMemo(() => {
    const seen = new Map();
    for (const a of (assignments || [])) {
      if (!seen.has(a.urid)) {
        seen.set(a.urid, { urid: a.urid, ur_name: a.ur_name });
      }
    }
    return [...seen.values()].sort((a, b) =>
      (a.ur_name || '').localeCompare(b.ur_name || '')
    );
  }, [assignments]);

  // Sites available for the currently-selected role.
  // If any assignment for this role has site_code = null, "All sites" is
  // offered as an option.
  const siteOptions = useMemo(() => {
    if (!selectedRole) return { hasAllSites: false, sites: [] };
    const urid = parseInt(selectedRole, 10);
    const sites = [];
    let hasAllSites = false;
    for (const a of (assignments || [])) {
      if (a.urid !== urid) continue;
      if (a.site_code === null || a.site_code === undefined) {
        hasAllSites = true;
      } else {
        sites.push({
          site_code: a.site_code,
          site_name: a.site_name || a.site_code,
        });
      }
    }
    sites.sort((a, b) => (a.site_name || '').localeCompare(b.site_name || ''));
    return { hasAllSites, sites };
  }, [assignments, selectedRole]);

  // ---------------------------------------------------------------------------
  // The same three lists in SearchSelect's { code, label } shape. Roles, sites
  // and versions are all named things rather than coded ones, so each uses
  // `label` — a urid or a version_id is machinery, not something to read.
  // ---------------------------------------------------------------------------
  const roleChoices = useMemo(
    () => roleOptions.map((r) => ({ code: String(r.urid), label: r.ur_name })),
    [roleOptions],
  );

  const siteChoices = useMemo(() => {
    const out = [];
    // "All sites" is the blank site code, and belongs at the top.
    if (siteOptions.hasAllSites) out.push({ code: '', label: 'All sites' });
    siteOptions.sites.forEach((s) =>
      out.push({ code: s.site_code, label: s.site_name, hint: s.site_code }));
    return out;
  }, [siteOptions]);

  const versionChoices = useMemo(() => {
    const out = versions.map((v) => ({
      code: v.version_id,
      label: `${v.version_code}${v.description ? ` — ${v.description}` : ''}${v.is_locked ? ' 🔒' : ''}`,
    }));
    // Keep the current selection visible if the list has not loaded yet.
    if (selectedVersion && !out.some((o) => o.code === selectedVersion)) {
      out.unshift({ code: selectedVersion, label: selectedVersion });
    }
    return out;
  }, [versions, selectedVersion]);

  // ---------------------------------------------------------------------------
  // Self-healing: keep selections consistent with available options
  // ---------------------------------------------------------------------------

  // If the selected role no longer exists in the user's assignments (e.g.
  // an admin revoked it), clear it.
  useEffect(() => {
    if (!selectedRole) return;
    const urid = parseInt(selectedRole, 10);
    const stillValid = roleOptions.some((r) => r.urid === urid);
    if (!stillValid) {
      setSelectedRole('');
      setSelectedSite('');
    }
  }, [roleOptions, selectedRole, setSelectedRole, setSelectedSite]);

  // If the selected site isn't valid for the current role, pick a sensible
  // default: the first specific site, or "All sites" if available.
  useEffect(() => {
    if (!selectedRole) return;
    const { hasAllSites, sites } = siteOptions;

    const currentIsAllSites = selectedSite === '' || selectedSite === null || selectedSite === undefined;
    const currentIsValid =
      (currentIsAllSites && hasAllSites) ||
      sites.some((s) => s.site_code === selectedSite);

    if (currentIsValid) return;

    if (sites.length > 0) {
      setSelectedSite(sites[0].site_code);
    } else if (hasAllSites) {
      setSelectedSite('');
    }
  }, [selectedRole, siteOptions, selectedSite, setSelectedSite]);

  // ---------------------------------------------------------------------------
  // User profile (uname safe-guarded against null)
  // ---------------------------------------------------------------------------
  const uname = localStorage.getItem('uname') || '';
  const initial = uname ? uname[0].toUpperCase() : '?';

  // ---------------------------------------------------------------------------
  // User options menu (theme + change password + log out)
  // ---------------------------------------------------------------------------
  const { theme, setTheme, options: themeOptions } = useTheme();
  const [menuOpen, setMenuOpen] = useState(false);
  const [showChangePassword, setShowChangePassword] = useState(false);
  const menuRef = useRef(null);

  // Close the menu when clicking outside of it.
  useEffect(() => {
    if (!menuOpen) return;
    const onDown = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [menuOpen]);

  return (
    <header className="h-[60px] bg-white dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700 flex items-center justify-between px-5 shadow-sm">

      {/* Left: Role + Site dropdowns (the company logo lives in the sidebar) */}
      <div className="flex items-center gap-4">

        {/* Role */}
        <div className="flex items-center gap-2">
          <label
            htmlFor="role-select"
            className="text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wide"
          >
            Role:
          </label>
          <SearchSelect
            id="role-select"
            autoWidth
            value={selectedRole || ''}
            onChange={(v) => v && setSelectedRole(v)}
            options={roleChoices}
            disabled={roleOptions.length === 0}
            clearable={false}
            placeholder={roleOptions.length === 0 ? 'No roles assigned' : 'Select Role'}
            emptyText="No role matches."
            ariaLabel="Role"
          />
        </div>

        {/* Site */}
        <div className="flex items-center gap-2">
          <label
            htmlFor="site-select"
            className="text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wide"
          >
            Site:
          </label>
          <SearchSelect
            id="site-select"
            autoWidth
            value={selectedSite || ''}
            onChange={setSelectedSite}
            options={siteChoices}
            disabled={!selectedRole}
            // "All sites" is a real choice, not an empty one — it is the blank
            // site code the api interceptor omits. So clearing is disabled and
            // it appears in the list on its own terms.
            clearable={false}
            placeholder={!selectedRole ? 'Select role first'
              : (siteChoices.length === 0 ? 'No sites for this role' : 'Select site')}
            emptyText="No site matches."
            ariaLabel="Site"
          />
        </div>

      </div>

      {/* Right: Version + profile */}
      <div className="flex items-center gap-4">

        {/* Version — loaded from bud_versions via /api/budgets/versions */}
        <div className="flex items-center gap-2">
          <label
            htmlFor="version-select"
            className="text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wide"
          >
            Version:
          </label>
          <SearchSelect
            id="version-select"
            className="w-56"
            value={selectedVersion || ''}
            onChange={(v) => v && setSelectedVersion(v)}
            options={versionChoices}
            disabled={versions.length === 0 && !selectedVersion}
            clearable={false}
            placeholder="Select Version"
            emptyText="No version matches."
            ariaLabel="Budget version"
          />
        </div>

        <div className="h-6 w-px bg-slate-200 dark:bg-slate-600 mx-2"></div>

        {/* User profile + options menu */}
        <div className="text-slate-600 dark:text-slate-300 text-sm font-medium">{uname}</div>
        <div className="relative" ref={menuRef}>
          <button
            type="button"
            className="w-[35px] h-[35px] bg-slate-200 dark:bg-slate-700 rounded-full flex justify-center items-center text-slate-600 dark:text-slate-200 font-bold cursor-pointer hover:bg-slate-300 dark:hover:bg-slate-600 transition-colors focus:outline-none focus:ring-2 focus:ring-slate-400"
            title="Account options"
            aria-haspopup="true"
            aria-expanded={menuOpen}
            onClick={() => setMenuOpen((o) => !o)}
          >
            {initial}
          </button>

          {menuOpen && (
            <div className="absolute right-0 mt-2 w-56 origin-top-right rounded-lg bg-white dark:bg-slate-800 shadow-lg ring-1 ring-black/5 border border-slate-200 dark:border-slate-700 z-30 overflow-hidden">
              {/* Signed-in identity */}
              <div className="px-4 py-3 border-b border-slate-100 dark:border-slate-700">
                <p className="text-xs text-slate-400 dark:text-slate-500 uppercase tracking-wide">Signed in as</p>
                <p className="text-sm font-medium text-slate-700 dark:text-slate-200 truncate">{uname || '—'}</p>
              </div>

              {/* Theme selector */}
              <div className="px-3 py-2 border-b border-slate-100 dark:border-slate-700">
                <p className="px-1 pb-1.5 text-[11px] font-medium text-slate-400 dark:text-slate-500 uppercase tracking-wide">
                  Appearance
                </p>
                <div className="grid grid-cols-3 gap-1">
                  {themeOptions.map((t) => (
                    <button
                      key={t.id}
                      type="button"
                      onClick={() => setTheme(t.id)}
                      className={`flex flex-col items-center gap-1 rounded-md py-1.5 text-xs transition-colors ${
                        theme === t.id
                          ? 'bg-blue-50 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400'
                          : 'text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700'
                      }`}
                    >
                      <span className="text-base leading-none">{t.icon}</span>
                      {t.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Actions */}
              <button
                type="button"
                onClick={() => { setMenuOpen(false); setShowChangePassword(true); }}
                className="w-full text-left px-4 py-2.5 text-sm text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
              >
                Change Password
              </button>
              <a
                href="/logout"
                className="block px-4 py-2.5 text-sm text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors border-t border-slate-100 dark:border-slate-700"
              >
                Log Out
              </a>
            </div>
          )}
        </div>

        {/* Product brand — anchored top-right (A) */}
        <div className="h-6 w-px bg-slate-200 dark:bg-slate-600 mx-1"></div>
        <MantleBrand heightClass="h-8" />
      </div>

      {showChangePassword && (
        <ChangePasswordModal onClose={() => setShowChangePassword(false)} />
      )}
    </header>
  );
};

export default TopHeader;